import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, prefersReducedMotion, preloadImage, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    const pending = this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    if (pending && typeof pending.catch === 'function') {
      void pending.catch(() => {});
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Native leave is more reliable than delegated on:pointerleave when exiting downward.
    if (this.refs.cardGallery?.hasAttribute('on:pointerenter')) {
      this.refs.cardGallery.addEventListener('pointerleave', this.#onGalleryPointerLeave);
    }

    this.#setupHoverVideoPrewarm();

    setTimeout(() => {
      this.#preloadHoverPreviewMedia();

      this.#queueSlideMediaSync();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hoverPrewarmObserver?.disconnect();
    this.#hoverPrewarmObserver = null;
    this.refs.cardGallery?.removeEventListener('pointerleave', this.#onGalleryPointerLeave);
    this.removeEventListener('click', this.navigateToProduct);
  }

  /** @type {IntersectionObserver | null} */
  #hoverPrewarmObserver = null;

  #onGalleryPointerLeave = (event) => this.resetImage(event);

  #setupHoverVideoPrewarm() {
    if (prefersReducedMotion() || !this.refs.cardGallery?.querySelector('deferred-media[data-card-hover-video]')) {
      return;
    }

    this.#hoverPrewarmObserver?.disconnect();
    this.#hoverPrewarmObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#warmCardHoverVideo();
          this.#hoverPrewarmObserver?.disconnect();
          this.#hoverPrewarmObserver = null;
          break;
        }
      },
      { rootMargin: '80px', threshold: 0.1 }
    );
    this.#hoverPrewarmObserver.observe(this);
  }

  #preloadHoverPreviewMedia() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const nextSlide = currentSlide?.nextElementSibling;
    nextSlide?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');

    const deferredMedia = nextSlide?.querySelector('deferred-media[data-card-hover-video]');
    if (deferredMedia && typeof deferredMedia.prepareContent === 'function') {
      deferredMedia.prepareContent();
    }
  }

  #warmCardHoverVideo() {
    if (prefersReducedMotion()) return;

    const { slideshow } = this.refs;
    const slides = slideshow?.refs?.slides;
    if (!Array.isArray(slides)) return;

    for (const slide of slides) {
      const deferredMedia = slide.querySelector('deferred-media[data-card-hover-video]');
      if (deferredMedia && typeof deferredMedia.prepareContent === 'function') {
        deferredMedia.prepareContent();
        return;
      }
    }
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();


    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    const variantPrefetch = this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    if (variantPrefetch && typeof variantPrefetch.catch === 'function') {
      void variantPrefetch.catch(() => {});
    }

    let newProductFromVariantPicker;
    if (event.target !== this.variantPicker) {
      newProductFromVariantPicker = this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // For combined listings, update the quick-add-component if the product changed
    // Check both event.detail.data.newProduct (from main variant picker) and newProductFromVariantPicker (from card variant picker)
    const newProduct = event.detail.data.newProduct ?? newProductFromVariantPicker;
    if (newProduct) {
      this.#updateQuickAddComponent(event.detail.data.html);
      this.#updateCardGallery(event.detail.data.html);
    } else {
      this.#updateVariantImages();
    }

    this.#previousSlideIndex = null;

    this.removeAttribute('data-no-swatch-selected');

    this.#updateOverflowList();

    if (newProduct) {
      this.#queueSlideMediaSync();
    }
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the quick-add-component when a combined listing switches to a different product.
   * @param {Document} html - The HTML document containing the new product card.
   */
  #updateQuickAddComponent(html) {
    const newQuickAdd = html.querySelector('quick-add-component');
    const currentQuickAdd = this.refs.quickAdd;

    if (!newQuickAdd || !currentQuickAdd) {
      return;
    }

    morph(currentQuickAdd, newQuickAdd, {
      ...MORPH_OPTIONS,
      childrenOnly: false,
    });
  }

  /**
   * Updates the card gallery when a combined listing switches to a different child product.
   * @param {Document} html - The HTML document from the section rendering response.
   */
  #updateCardGallery(html) {
    const newGallery = html.querySelector('.card-gallery');
    const currentGallery = this.refs.cardGallery;

    if (!newGallery || !currentGallery) return;

    const newMediaRoot = newGallery.querySelector(':scope > a.contents, :scope > product-title');
    const currentMediaRoot = currentGallery.querySelector(':scope > a.contents, :scope > product-title');

    if (newMediaRoot && currentMediaRoot) {
      morph(currentMediaRoot, newMediaRoot, MORPH_OPTIONS);
    }

    const aspectRatio = newGallery.style.getPropertyValue('--gallery-aspect-ratio');
    if (aspectRatio) {
      currentGallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);
    }

    if (newGallery.dataset.productId) {
      currentGallery.dataset.productId = newGallery.dataset.productId;
    }
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (!newPriceElement || !priceContainer) return;

    const productPrice = priceContainer.closest('product-price');

    morph(priceContainer, newPriceElement);
    this.#updatePromoMessaging(productPrice, event.detail.data.html);
  }

  /**
   * Updates the discount promo messaging that sits alongside the price.
   * @param {Element | null} productPrice - The price element wrapping the promo messaging.
   * @param {Document} html - The HTML document from the section rendering response.
   */
  #updatePromoMessaging(productPrice, html) {
    if (!productPrice) return;

    const currentPromo = productPrice.querySelector('.price-promo-messaging');
    const newPromo = html.querySelector('.price-promo-messaging');

    if (!newPromo) {
      currentPromo?.remove();
      return;
    }

    if (currentPromo) {
      morph(currentPromo, newPromo);
      return;
    }

    productPrice
      .querySelector(`[ref='priceContainer']`)
      ?.insertAdjacentElement('afterend', /** @type {Element} */ (newPromo.cloneNode(true)));
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }

    this.#queueSlideMediaSync();
  };

  #queueSlideMediaSync() {
    requestAnimationFrame(() => this.#syncSlideMediaPlayback());
  }

  #syncSlideMediaPlayback() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const slides = slideshow.refs?.slides;
    if (!Array.isArray(slides) || slides.length === 0) return;

    for (const slide of slides) {
      const deferredMedia = slide.querySelector('deferred-media');
      if (!(deferredMedia instanceof HTMLElement)) continue;

      const isActiveSlide = slide.getAttribute('aria-hidden') === 'false';
      const isCardHoverVideo = deferredMedia.hasAttribute('data-card-hover-video');
      const isAutoplayMedia = deferredMedia.hasAttribute('autoplay');
      const shouldAutoPlay = isCardHoverVideo || isAutoplayMedia;
      const pauseMedia = deferredMedia.pauseMedia;
      const playMedia = deferredMedia.playMedia;
      const loadContent = deferredMedia.loadContent;

      if (typeof pauseMedia !== 'function') continue;

      if (isActiveSlide) {
        if (!shouldAutoPlay) {
          continue;
        }

        if (isCardHoverVideo && prefersReducedMotion()) {
          pauseMedia.call(deferredMedia);
          continue;
        }

        if (isCardHoverVideo && typeof loadContent === 'function') {
          if (typeof deferredMedia.prepareContent === 'function' && !deferredMedia.getAttribute('data-media-loaded')) {
            deferredMedia.prepareContent();
          } else {
            loadContent.call(deferredMedia, false);
          }
        }

        if (typeof playMedia === 'function') {
          playMedia.call(deferredMedia);
        }

        continue;
      }

      pauseMedia.call(deferredMedia);
      if (isCardHoverVideo && typeof deferredMedia.clearMediaReady === 'function') {
        deferredMedia.clearMediaReady();
      }
    }
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   * @param {PointerEvent} [event] - The pointerenter event that triggered the preview.
   */
  previewVariant(id, event) {
    this.#preloadSwatchMedia(event);

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Preloads the swatch's linked media so combined-listing variant switches (where the
   * hovered media isn't part of this card's own gallery and can't be previewed live)
   * display instantly once the variant is actually selected.
   * @param {PointerEvent} [event] - The pointerenter event.
   */
  #preloadSwatchMedia(event) {
    if (!(event?.target instanceof Element)) return;

    const preloadSrc = event.target.closest('[data-featured-media-src]')?.getAttribute('data-featured-media-src');
    if (preloadSrc) preloadImage(preloadSrc);
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    this.#warmCardHoverVideo();

    const slides = slideshow.slides;
    let hoverSlideIndex = null;
    if (Array.isArray(slides) && slides.length > 1) {
      for (let index = 0; index < slides.length; index++) {
        if (slides[index].querySelector('deferred-media[data-card-hover-video]')) {
          hoverSlideIndex = index;
          break;
        }
      }
      if (hoverSlideIndex == null) hoverSlideIndex = 1;
    }

    if (hoverSlideIndex != null) {
      slideshow.select(hoverSlideIndex, undefined, { animate: false });
    } else if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
    }

    setTimeout(() => this.#preloadHoverPreviewMedia());
    this.#queueSlideMediaSync();
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    this.#resetVariant();
    this.#queueSlideMediaSync();
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
